import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import eventAPI from '../services/eventAPI';
import { authAPI } from '../services/api';
import { parseBackendDatetime } from '../utils/dateUtils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RetroTextarea from '../components/ui/RetroTextarea';
import ImageLightbox from '../components/ImageLightbox';
import './EventRecap.css';

function EventRecap() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Blog post state
  const [blogPosts, setBlogPosts] = useState([]);
  const [newBlogPost, setNewBlogPost] = useState('');
  const [savingPost, setSavingPost] = useState(false);

  // Image upload state
  const [images, setImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedEvent, setEditedEvent] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);

  useEffect(() => {
    loadEventData();
    loadFamilyMembers();
  }, [eventId]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      const [eventData, userData, imagesData, blogPostsData] = await Promise.all([
        eventAPI.getEvent(eventId),
        authAPI.getCurrentUser(),
        eventAPI.getEventImages(eventId),
        eventAPI.getBlogPosts(eventId)
      ]);

      setEvent(eventData);
      setUser(userData);
      setImages(imagesData.images || []);
      setBlogPosts(blogPostsData.blog_posts || []);
    } catch (error) {
      console.error('Failed to load event:', error);
      setError('Event konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const data = await eventAPI.getFamilyMembers();
      setFamilyMembers(data.family_members || []);
    } catch (error) {
      console.error('Failed to load family members:', error);
    }
  };

  const handleAddBlogPost = async (e) => {
    e.preventDefault();
    if (!newBlogPost.trim()) return;

    try {
      setSavingPost(true);
      const response = await eventAPI.createBlogPost(eventId, newBlogPost);
      setBlogPosts([response.blog_post, ...blogPosts]);
      setNewBlogPost('');
    } catch (error) {
      console.error('Failed to add blog post:', error);
      alert('Blogeintrag konnte nicht gespeichert werden');
    } finally {
      setSavingPost(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingImage(true);
      const formData = new FormData();
      for (let file of files) {
        formData.append('images', file);
      }

      const response = await eventAPI.uploadEventImages(eventId, formData);
      setImages([...images, ...response.images]);
    } catch (error) {
      console.error('Failed to upload images:', error);
      alert('Bilder konnten nicht hochgeladen werden');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handleStartEdit = () => {
    setEditedEvent({
      title: event.title,
      description: event.description || '',
      finalized_datetime: event.finalized_datetime || '',
      finalized_location: event.finalized_location || '',
      color: event.color,
      invited_user_ids: event.invitations?.map(inv => inv.user_id) || []
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedEvent(null);
  };

  const handleSaveEdit = async () => {
    if (!editedEvent) return;

    try {
      setSavingEdit(true);
      await eventAPI.updateEvent(eventId, editedEvent);

      // Reload event data
      await loadEventData();
      setIsEditing(false);
      setEditedEvent(null);
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Event konnte nicht aktualisiert werden');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedEvent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="event-recap-container">
          <div className="loading">Lädt...</div>
        </div>
      </>
    );
  }

  if (error || !event) {
    return (
      <>
        <Navbar />
        <div className="event-recap-container">
          <div className="error">{error || 'Event nicht gefunden'}</div>
          <button className="retro-button" onClick={() => navigate('/calendar')}>
            Zurück zum Kalender
          </button>
        </div>
      </>
    );
  }

  const colorMap = {
    purple: '#9d5cff',
    pink: '#ec4899',
    blue: '#3b82f6',
    green: '#10b981',
    orange: '#f59e0b',
    red: '#ef4444'
  };
  const eventColor = colorMap[event.color] || event.color;
  const isCreator = user && event && user.id === event.creator_id;

  // Debug logging
  console.log('EventRecap Debug:', {
    user,
    event,
    userId: user?.id,
    creatorId: event?.creator_id,
    isCreator,
    comparison: `${user?.id} === ${event?.creator_id}`
  });

  return (
    <>
      <Navbar />
      <div className="event-recap-container">
        {/* Header */}
        <div className="recap-header">
          <div className="recap-header-top">
            <button className="back-button" onClick={() => navigate('/calendar')}>
              ← ZURÜCK
            </button>
            {isCreator && !isEditing && (
              <button className="retro-button retro-button-primary" onClick={handleStartEdit}>
                BEARBEITEN
              </button>
            )}
            {isEditing && (
              <div className="edit-buttons">
                <button
                  className="retro-button retro-button-primary"
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                >
                  {savingEdit ? 'SPEICHERN...' : 'SPEICHERN'}
                </button>
                <button
                  className="retro-button"
                  onClick={handleCancelEdit}
                  disabled={savingEdit}
                >
                  ABBRECHEN
                </button>
              </div>
            )}
          </div>
          <div className="recap-header-content">
            <div className="recap-title-wrapper">
              <span className="recap-color-dot" style={{ backgroundColor: eventColor }}></span>
              {isEditing ? (
                <input
                  type="text"
                  className="recap-title-input"
                  value={editedEvent?.title || ''}
                  onChange={(e) => handleEditChange('title', e.target.value)}
                />
              ) : (
                <h1 className="recap-title">{event.title}</h1>
              )}
            </div>
            <div className="recap-meta">
              {isEditing ? (
                <>
                  <div className="edit-field">
                    <label>[#] DATUM & UHRZEIT:</label>
                    <input
                      type="datetime-local"
                      className="recap-input"
                      value={editedEvent?.finalized_datetime ? parseBackendDatetime(editedEvent.finalized_datetime) : ''}
                      onChange={(e) => handleEditChange('finalized_datetime', e.target.value)}
                    />
                  </div>
                  <div className="edit-field">
                    <label>[@] ORT:</label>
                    <input
                      type="text"
                      className="recap-input"
                      value={editedEvent?.finalized_location || ''}
                      onChange={(e) => handleEditChange('finalized_location', e.target.value)}
                      placeholder="Ort eingeben..."
                    />
                  </div>
                </>
              ) : (
                <>
                  {event.finalized_datetime && (
                    <span className="recap-date">
                      [#] {new Date(event.finalized_datetime).toLocaleString('de-DE')}
                    </span>
                  )}
                  {event.finalized_location && (
                    <span className="recap-location">
                      [@] {event.finalized_location}
                    </span>
                  )}
                  {event.accepted_users && event.accepted_users.length > 0 && (
                    <span className="recap-participants">
                      [&] DABEI WAREN: {event.accepted_users.map(u => u.username).join(', ')}
                    </span>
                  )}
                </>
              )}
            </div>
            {isEditing ? (
              <>
                <div className="edit-field">
                  <label>BESCHREIBUNG:</label>
                  <textarea
                    className="recap-textarea"
                    value={editedEvent?.description || ''}
                    onChange={(e) => handleEditChange('description', e.target.value)}
                    placeholder="Beschreibung eingeben..."
                    rows="3"
                  />
                </div>
                <div className="edit-field">
                  <label>[&] TEILNEHMER / EINGELADENE:</label>
                  <div className="participants-select">
                    {familyMembers.map(member => (
                      <label key={member.id} className="participant-checkbox">
                        <input
                          type="checkbox"
                          checked={editedEvent?.invited_user_ids?.includes(member.id) || false}
                          onChange={(e) => {
                            const currentIds = editedEvent?.invited_user_ids || [];
                            const newIds = e.target.checked
                              ? [...currentIds, member.id]
                              : currentIds.filter(id => id !== member.id);
                            handleEditChange('invited_user_ids', newIds);
                          }}
                        />
                        <span>{member.username}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              event.description && (
                <p className="recap-description">{event.description}</p>
              )
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="recap-content">
          {/* Images Section */}
          <section className="recap-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="section-icon">[*]</span>
                BILDER
              </h2>
              <label className="retro-button retro-button-primary upload-label">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingImage}
                />
                {uploadingImage ? 'LADE HOCH...' : '+ BILDER HOCHLADEN'}
              </label>
            </div>

            {images.length > 0 ? (
              <div className="images-grid">
                {images.map((image, index) => (
                  <div
                    key={image.id}
                    className="image-card"
                    onClick={() => handleImageClick(index)}
                  >
                    <img src={eventAPI.getImageUrl(image.file_path)} alt={image.filename} />
                    {image.caption && (
                      <p className="image-caption">"{image.caption}"</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>Noch keine Bilder hochgeladen</p>
              </div>
            )}
          </section>

          {/* Blog Posts Section */}
          <section className="recap-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="section-icon">[+]</span>
                ERINNERUNGEN
              </h2>
            </div>

            {/* New Blog Post Form */}
            <form onSubmit={handleAddBlogPost} className="blog-post-form">
              <RetroTextarea
                value={newBlogPost}
                onChange={(e) => setNewBlogPost(e.target.value)}
                placeholder="Schreibe eine Erinnerung an dieses Event..."
                rows={4}
              />
              <button
                type="submit"
                className="retro-button retro-button-primary"
                disabled={savingPost || !newBlogPost.trim()}
              >
                {savingPost ? 'SPEICHERE...' : 'EINTRAG HINZUFÜGEN'}
              </button>
            </form>

            {/* Blog Posts List */}
            {blogPosts.length > 0 ? (
              <div className="blog-posts-list">
                {blogPosts.map(post => (
                  <div key={post.id} className="blog-post-card">
                    <div className="blog-post-header">
                      <span className="blog-post-author">{post.username}</span>
                      <span className="blog-post-date">
                        {new Date(post.created_at).toLocaleString('de-DE')}
                      </span>
                    </div>
                    <p className="blog-post-content">{post.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>Noch keine Einträge vorhanden</p>
              </div>
            )}
          </section>
        </div>

        <Footer />
      </div>

      {/* Image Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <ImageLightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={handleCloseLightbox}
          eventId={eventId}
        />
      )}
    </>
  );
}

export default EventRecap;
